/**
 * Solar Live & Performance Analytics API Service
 * Connects frontend dashboard components to Django REST endpoints
 */

const getApiBaseUrl = (): string => {
  const envUrl = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
  return envUrl.replace(/\/+$/, '');
};

export interface KpiCardData {
  id: string;
  title: string;
  value?: number;
  expected_kw?: number;
  actual_kw?: number;
  ratio_pct?: number;
  import_kwh?: number | null;
  net_kwh?: number;
  unit?: string;
  timestamp?: string;
  peak_timestamp?: string;
  time_window?: string;
  display_text: string;
}

export interface Row3ButtonData {
  title: string;
  value?: number | string;
  ratio?: number;
  status?: 'good' | 'watch' | 'bad';
  display_text: string;
  formula?: string;
  rules?: string;
  actual_ipr?: number;
  expected_median_ipr?: number;
  timestamp?: string;
}

export interface KpiCardsResponse {
  /** false when the selected dates have no rows (the payload is then empty, never another range's data) */
  has_data?: boolean;
  success: boolean;
  data_timestamp?: string;
  is_live: boolean;
  data_availability_pct: number;
  dc_capacity_mwp: number;
  cards: Record<string, KpiCardData>;
  row3_buttons?: Record<string, Row3ButtonData>;
}

export interface PowerCurvePeakInfo {
  peak_power_mw: number;
  peak_time: string;
  peak_datetime: string;
}

export interface PowerCurvePoint {
  datetime: string;
  time: string;
  formatted_time?: string;
  actual_power_kw: number;
  expected_power_kw: number;
  plant_active_power_mw?: number;
  expected_plant_active_power_mw?: number;
  plant_reactive_power_mvar?: number;
  mcr_gir_wpm2?: number;
  b1_icr13_gir_wpm2?: number;
  b2_icr17_gir_wpm2?: number;
  b3_icr01_gir_wpm2?: number;
  b4_icr01_gir_wpm2?: number;
  b5_icr07_gir_wpm2?: number;
  plant_peak_active_power_mw?: number;
  radiation_w_per_sqm: number;
  ambient_temp_degc?: number;
  module_temp_degc?: number;
  ipr_pct?: number;
  // Extra SQL telemetry columns from dbo.axonmetric_livedaycons
  grid_voltage_kv?: number;
  grid_current_a?: number;
  reactive_power_mvar?: number;
  power_factor?: number;
  frequency_hz?: number;
  total_energy_mwh?: number;
  export_energy_mwh?: number;
  import_energy_mwh?: number;
  peak_power_kw?: number;
  plant_pa_pct?: number;
  plant_ga_pct?: number;
  plant_rh_pct?: number;
  windspeed_mps?: number;
  gii_whpm2?: number;
  ghi_whpm2?: number;
  [key: string]: any;
}

export interface DailyEnergyPoint {
  date: string;
  date_label: string;
  export_energy_kwh: number;
  export_energy_int?: number;
  export_energy_mwh?: number;
  gii_kwh_per_sqm: number;
  generation_kwh?: number;
  generation_mwh?: number;
  consumption_kwh?: number;
  consumption_mwh?: number;
  total_energy_mwh?: number;
  insolation?: number;
  pr?: number;
  cuf?: number;
  plant?: string;
  [key: string]: any;
}

export interface WeatherPoint {
  datetime: string;
  time: string;
  value: number;
  parameter: string;
  unit: string;
}

export interface RadiationVsIprPoint {
  datetime: string;
  time: string;
  radiation_w_per_sqm: number;
  ipr_pct: number;
  active_power_kw: number;
}

export interface InverterGridRow {
  datetime: string;
  raw_datetime?: string;
  inv: string;
  dc_load_mwp?: number | null;
  radiation_w_per_sqm?: number | null;
  dc_power_kw?: number;
  ac_power_kw?: number;
  ac_voltage_v?: number;
  energy_wh?: number;
  energy_kwh?: number;
  energy_wh_formatted?: string;
  energy_kwh_formatted?: string;
  pr_pct?: number | null;
  status?: string;
}

export interface InverterAnalyticsResponse {
  /** false when the selected dates have no rows (the payload is then empty, never another range's data) */
  has_data?: boolean;
  success: boolean;
  start_date: string;
  end_date: string;
  curve_date: string;
  dates?: Array<{ key: string; raw_date: string; date_full: string; date_label: string; color: string }>;
  inverters?: string[];
  daily_comparison: Array<Record<string, any>>;
  inverter_day_by_day?: Array<Record<string, any>>;
  stacked_comparison?: Array<Record<string, any>>;
  power_curves: Array<Record<string, any>>;
  weather_series?: Array<Record<string, any>>;
  weather?: Array<Record<string, any>>;
  heat_map?: Array<{
    inverter: string;
    pr_pct: number;
    live_pr?: number;
    active_power_kw: number;
    energy_kwh: number;
    max_power_kw: number;
    ac_voltage_v: number;
    dc_voltage_v: number;
    voltage_ac?: number;
    voltage_dc?: number;
    efficiency_pct: number;
    status: string;
    dc_load_mwp?: number;
  }>;
  scatter_treemap: Array<{
    inverter: string;
    energy_kwh: number;
    pr_pct: number;
    max_power_kw: number;
    energy_delta?: number;
    pr_delta?: number;
    active_power_kw?: number;
    status?: string;
  }>;
  icr_inverter_energy?: Array<{
    icr: string;
    inverter: string;
    label: string;
    energy_kwh: number;
  }>;
  igbt_performance?: Array<{
    icr: string;
    inverter: string;
    label: string;
    igbt1_kw: number;
    igbt2_kw: number;
  }>;
  temperature_profile?: Array<{
    datetime: string;
    time: string;
    cabinet_temp_degc: number;
    igbt_temp_degc: number;
    idt_wti_degc: number;
    idt_oti_degc: number;
  }>;
  power_vs_voltage?: Array<{
    datetime: string;
    time: string;
    active_power_kw: number;
    voltage_v: number;
  }>;
  active_vs_reactive?: Array<{
    datetime: string;
    time: string;
    active_power_kw: number;
    reactive_power_kvar: number;
  }>;
}

export interface ScbHeatMapUnit {
  icr: string;
  inverter: string;
  inv?: string;
  scb: string;
  scb_name_in_bi?: string;
  full_id: string;
  norm_current_a: number;
  total_current_a: number;
  radiation_wpm2: number;
  strings?: number;
  dc_load_mwp?: number;
  displayName?: string;
  scb_count?: number;
}

export interface ScbCurrentProfilePoint {
  datetime: string;
  time: string;
  scb_current_a: number;
  mcr_gir_wpm2: number;
  block_gir_wpm2?: number;
  b1_icr13_gir_wpm2: number;
  b2_icr17_gir_wpm2: number;
  b3_icr01_gir_wpm2: number;
  b4_icr01_gir_wpm2: number;
  b5_icr07_gir_wpm2: number;
  block_tamb_degc?: number;
  block_tm_degc?: number;
  norm_current_a?: number;
  strings?: number;
  dc_load_mwp?: number;
  [key: string]: any;
}

export interface ScbSeriesItem {
  key: string;
  name: string;
  label: string;
  strings: number;
  dc_load_mwp: number;
  color: string;
}

export interface ScbAnalyticsResponse {
  /** false when the selected dates have no rows (the payload is then empty, never another range's data) */
  has_data?: boolean;
  success: boolean;
  target_date?: string;
  selected_scb?: string | null;
  selected_inv?: string | null;
  inverters_list?: string[];
  inverter_heat_map?: ScbHeatMapUnit[];
  heat_map: ScbHeatMapUnit[];
  current_profile: ScbCurrentProfilePoint[];
  scb_series?: ScbSeriesItem[];
  scb_list: string[];
}

interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

const memoryCache = new Map<string, CacheEntry<any>>();
const inFlightRequests = new Map<string, Promise<any>>();
const DEFAULT_TTL_MS = 5 * 60 * 1000; // 5 minutes default cache

async function cachedFetch<T>(
  url: string,
  options?: { bypassCache?: boolean; ttlMs?: number }
): Promise<T> {
  const cacheKey = url;
  const now = Date.now();
  const ttl = options?.ttlMs ?? DEFAULT_TTL_MS;

  if (!options?.bypassCache) {
    if (memoryCache.has(cacheKey)) {
      const entry = memoryCache.get(cacheKey)!;
      if (now - entry.timestamp < ttl) {
        return entry.data as T;
      }
    }
    // Check sessionStorage for instant retrieval (only if not bypassCache)
    try {
      const raw = sessionStorage.getItem(`api_cache_${cacheKey}`);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (now - parsed.timestamp < ttl) {
          memoryCache.set(cacheKey, parsed);
          return parsed.data as T;
        }
      }
    } catch {}
  }

  // An identical request already on the wire is fresh by definition, so bypassCache callers share it too.
  if (inFlightRequests.has(cacheKey)) {
    return inFlightRequests.get(cacheKey)! as Promise<T>;
  }

  const promise = (async () => {
    try {
      const res = await fetch(url);
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}: Failed to fetch ${url}`);
      }
      const json = await res.json();

      // Do not store empty data in cache so next fetch tries fresh
      const isEmpty = !json ||
        (Array.isArray(json) && json.length === 0) ||
        ((json as any)?.count === 0 && Array.isArray((json as any)?.data) && (json as any)?.data.length === 0) ||
        ((json as any)?.inverters && Array.isArray((json as any)?.inverters) && (json as any)?.inverters.length === 0) ||
        ((json as any)?.power_curves && Array.isArray((json as any)?.power_curves) && (json as any)?.power_curves.length === 0);

      const entry = { data: json, timestamp: Date.now() };
      if (!isEmpty) {
        memoryCache.set(cacheKey, entry);
        try {
          const serialized = JSON.stringify(entry);
          // Only save in sessionStorage if under 150KB to avoid thread hitching and storage quota exceptions
          if (serialized.length < 150000) {
            sessionStorage.setItem(`api_cache_${cacheKey}`, serialized);
          }
        } catch {}
      }
      return json as T;
    } catch (fetchErr) {
      throw fetchErr;
    } finally {
      inFlightRequests.delete(cacheKey);
    }
  })();

  inFlightRequests.set(cacheKey, promise);
  return promise;
}

/** Exact-day list and precise bounds of the dashboard date filter (see Dashboard/dateFilter.ts). */
const appendDateSelection = (
  query: URLSearchParams,
  params?: { dates?: string; start_datetime?: string; end_datetime?: string },
) => {
  if (params?.dates) query.append('dates', params.dates);
  if (params?.start_datetime) query.append('start_datetime', params.start_datetime);
  if (params?.end_datetime) query.append('end_datetime', params.end_datetime);
};

export interface AvailableDate {
  date: string;
  date_label: string;
  record_count: number;
}

export interface TableColumnMeta {
  key: string;
  label: string;
  type: 'numeric' | 'timestamp' | 'category';
  unit?: string;
  data_type?: string;
  color?: string;
}

export interface TableColumnsResponse {
  success: boolean;
  table_name: string;
  total_columns: number;
  columns: TableColumnMeta[];
}

export const solarLiveApi = {
  clearCache(): void {
    memoryCache.clear();
    inFlightRequests.clear();
    try {
      Object.keys(sessionStorage).forEach((key) => {
        if (key.startsWith('api_cache_')) {
          sessionStorage.removeItem(key);
        }
      });
    } catch {}
  },

  async getTableColumns(params?: { plant_id?: string; table?: 'livedaycons' | 'inverter' | string; bypass_cache?: boolean }): Promise<TableColumnsResponse> {
    const query = new URLSearchParams();
    if (params?.plant_id) query.append('plant_id', params.plant_id);
    if (params?.table) query.append('table', params.table);

    const url = `${getApiBaseUrl()}/v1/solar-live/table-columns/?${query.toString()}`;
    return await cachedFetch(url, { bypassCache: params?.bypass_cache, ttlMs: 15 * 60 * 1000 });
  },

  async getAvailableDates(params?: { plant_id?: string; table?: 'plant' | 'inverter' | 'scb'; bypass_cache?: boolean }): Promise<{ success: boolean; data: AvailableDate[]; latest_datetime?: string | null }> {
    const query = new URLSearchParams();
    if (params?.plant_id) query.append('plant_id', params.plant_id);
    if (params?.table) query.append('table', params.table);

    const url = `${getApiBaseUrl()}/v1/solar-live/available-dates/?${query.toString()}`;
    return await cachedFetch(url, { bypassCache: params?.bypass_cache, ttlMs: 10 * 60 * 1000 });
  },

  async getKpiCards(params?: { plant_id?: string; start_date?: string; end_date?: string; dates?: string; start_datetime?: string; end_datetime?: string; bypass_cache?: boolean }): Promise<KpiCardsResponse> {
    const query = new URLSearchParams();
    if (params?.plant_id) query.append('plant_id', params.plant_id);
    if (params?.start_date) query.append('start_date', params.start_date);
    if (params?.end_date) query.append('end_date', params.end_date);
    appendDateSelection(query, params);

    const url = `${getApiBaseUrl()}/v1/solar-live/kpi-cards/?${query.toString()}`;
    return await cachedFetch(url, { bypassCache: params?.bypass_cache });
  },

  async getPowerCurve(params?: { plant_id?: string; date?: string; start_date?: string; end_date?: string; dates?: string; start_datetime?: string; end_datetime?: string; bypass_cache?: boolean }): Promise<{ success: boolean; data: PowerCurvePoint[]; peak_info?: PowerCurvePeakInfo }> {
    const query = new URLSearchParams();
    if (params?.plant_id) query.append('plant_id', params.plant_id);
    if (params?.date) query.append('date', params.date);
    if (params?.start_date) query.append('start_date', params.start_date);
    if (params?.end_date) query.append('end_date', params.end_date);
    appendDateSelection(query, params);

    const url = `${getApiBaseUrl()}/v1/solar-live/power-curve/?${query.toString()}`;
    return await cachedFetch(url, { bypassCache: params?.bypass_cache });
  },

  async getDailyEnergy(params?: { plant_id?: string; start_date?: string; end_date?: string; dates?: string; start_datetime?: string; end_datetime?: string; bypass_cache?: boolean }): Promise<{ success: boolean; data: DailyEnergyPoint[] }> {
    const query = new URLSearchParams();
    if (params?.plant_id) query.append('plant_id', params.plant_id);
    if (params?.start_date) query.append('start_date', params.start_date);
    if (params?.end_date) query.append('end_date', params.end_date);
    appendDateSelection(query, params);

    const url = `${getApiBaseUrl()}/v1/solar-live/daily-energy/?${query.toString()}`;
    return await cachedFetch(url, { bypassCache: params?.bypass_cache });
  },

  async getWeather(params?: { plant_id?: string; date?: string; start_date?: string; end_date?: string; dates?: string; start_datetime?: string; end_datetime?: string; parameter?: string; bypass_cache?: boolean }): Promise<{
    success: boolean;
    parameter: string;
    unit: string;
    available_parameters: string[];
    data: WeatherPoint[];
  }> {
    const query = new URLSearchParams();
    if (params?.plant_id) query.append('plant_id', params.plant_id);
    if (params?.date) query.append('date', params.date);
    if (params?.start_date) query.append('start_date', params.start_date);
    if (params?.end_date) query.append('end_date', params.end_date);
    appendDateSelection(query, params);
    if (params?.parameter) query.append('parameter', params.parameter);

    const url = `${getApiBaseUrl()}/v1/solar-live/weather/?${query.toString()}`;
    return await cachedFetch(url, { bypassCache: params?.bypass_cache });
  },

  async getRadiationVsIpr(params?: { plant_id?: string; date?: string; start_date?: string; end_date?: string; dates?: string; start_datetime?: string; end_datetime?: string; bypass_cache?: boolean }): Promise<{ success: boolean; data: RadiationVsIprPoint[] }> {
    const query = new URLSearchParams();
    if (params?.plant_id) query.append('plant_id', params.plant_id);
    if (params?.date) query.append('date', params.date);
    if (params?.start_date) query.append('start_date', params.start_date);
    if (params?.end_date) query.append('end_date', params.end_date);
    appendDateSelection(query, params);

    const url = `${getApiBaseUrl()}/v1/solar-live/radiation-vs-ipr/?${query.toString()}`;
    return await cachedFetch(url, { bypassCache: params?.bypass_cache });
  },

  async getInverterTelemetryGrid(params?: {
    plant_id?: string;
    datetime?: string;
    date?: string;
    start_date?: string;
    end_date?: string; dates?: string; start_datetime?: string; end_datetime?: string;
    inverter?: string;
    limit?: number;
    bypass_cache?: boolean;
  }): Promise<{
    success: boolean;
    count: number;
    data: InverterGridRow[];
  }> {
    const query = new URLSearchParams();
    if (params?.plant_id) query.append('plant_id', params.plant_id);
    if (params?.datetime) query.append('datetime', params.datetime);
    if (params?.date) query.append('date', params.date);
    if (params?.start_date) query.append('start_date', params.start_date);
    if (params?.end_date) query.append('end_date', params.end_date);
    appendDateSelection(query, params);
    if (params?.inverter) query.append('inverter', params.inverter);
    if (params?.limit) query.append('limit', String(params.limit));

    const url = `${getApiBaseUrl()}/v1/solar-live/inverters/telemetry-grid/?${query.toString()}`;
    return await cachedFetch(url, { bypassCache: params?.bypass_cache !== undefined ? params.bypass_cache : true });
  },

  async getInverterAnalytics(params?: { plant_id?: string; date?: string; start_date?: string; end_date?: string; dates?: string; start_datetime?: string; end_datetime?: string; bypass_cache?: boolean }): Promise<InverterAnalyticsResponse> {
    const query = new URLSearchParams();
    if (params?.plant_id) query.append('plant_id', params.plant_id);
    if (params?.date) query.append('date', params.date);
    if (params?.start_date) query.append('start_date', params.start_date);
    if (params?.end_date) query.append('end_date', params.end_date);
    appendDateSelection(query, params);

    const url = `${getApiBaseUrl()}/v1/solar-live/inverters/analytics/?${query.toString()}`;
    return await cachedFetch(url, { bypassCache: params?.bypass_cache });
  },

  async getScbAnalytics(params?: {
    plant_id?: string;
    date?: string;
    start_date?: string;
    end_date?: string; dates?: string; start_datetime?: string; end_datetime?: string;
    scb?: string;
    inv?: string;
    icr?: string;
    bypass_cache?: boolean;
  }): Promise<ScbAnalyticsResponse> {
    const query = new URLSearchParams();
    if (params?.plant_id) query.append('plant_id', params.plant_id);
    if (params?.date) query.append('date', params.date);
    if (params?.start_date) query.append('start_date', params.start_date);
    if (params?.end_date) query.append('end_date', params.end_date);
    appendDateSelection(query, params);
    if (params?.scb) query.append('scb', params.scb);
    if (params?.inv) query.append('inv', params.inv);
    if (params?.icr) query.append('icr', params.icr);

    const url = `${getApiBaseUrl()}/v1/solar-live/scb/analytics/?${query.toString()}`;
    return await cachedFetch(url, { bypassCache: params?.bypass_cache });
  },

  async checkInverterPipeline(plant_id = 'AXON018'): Promise<any> {
    const url = `${getApiBaseUrl()}/v1/solar-live/inverters/check/?plant_id=${plant_id}`;
    try {
      const res = await fetch(url);
      const json = await res.json();
      console.log('%c[SolarLive Pipeline Check]', 'color: #9333EA; font-weight: bold; font-size: 13px;', json);
      return json;
    } catch (err) {
      console.error('[SolarLive Pipeline Check Error]', err);
      return { status: 'FAILED', error: String(err) };
    }
  },

  async getCompanyKpis(params?: { company_id?: string | number; company_code?: string; bypass_cache?: boolean }): Promise<{
    success: boolean;
    company_id?: string | number;
    sites: Record<string, {
      site_id: number;
      site_code: string;
      site_name: string;
      peak_power_kw?: number | null;
      peak_power_mw?: number | null;
      today_energy_kwh?: number | null;
      today_energy_mwh?: number | null;
      today_input_gii?: number | null;
      today_input_mwh?: number | null;
      plf_percent?: number | null;
      pr_percent?: number | null;
      data_timestamp?: string;
      is_live?: boolean;
    }>;
  }> {
    const query = new URLSearchParams();
    if (params?.company_id) query.append('company_id', String(params.company_id));
    if (params?.company_code) query.append('company_code', params.company_code);

    const url = `${getApiBaseUrl()}/v1/solar-live/company-kpis/?${query.toString()}`;
    return await cachedFetch(url, { bypassCache: params?.bypass_cache });
  },

  async downloadFullTelemetryZip(params: {
    plant_id?: string;
    start_date?: string;
    end_date?: string; dates?: string; start_datetime?: string; end_datetime?: string;
    table?: 'axonmetric_livedaycons' | 'axonmetric_livestringinv' | 'plant' | 'inverter' | 'central inverter' | 'scb';
    inverter?: string;
  }): Promise<{ success: boolean; error?: string }> {
    const query = new URLSearchParams();
    if (params?.plant_id) query.append('plant_id', params.plant_id);
    if (params?.start_date) query.append('start_date', params.start_date);
    if (params?.end_date) query.append('end_date', params.end_date);
    appendDateSelection(query, params);
    if (params?.table) query.append('table', params.table);
    if (params?.inverter) query.append('inverter', params.inverter);

    const url = `${getApiBaseUrl()}/v1/solar-live/export-telemetry/?${query.toString()}`;
    try {
      const response = await fetch(url);
      if (!response.ok) {
        let errMessage = `Export failed with HTTP ${response.status}`;
        try {
          const errJson = await response.json();
          if (errJson?.error) errMessage = errJson.error;
        } catch {}
        throw new Error(errMessage);
      }

      const disposition = response.headers.get('Content-Disposition');
      let filename = 'AxonMetric_Live_Export.zip';
      if (disposition && disposition.includes('filename=')) {
        const matches = disposition.match(/filename="?([^";]+)"?/);
        if (matches && matches[1]) filename = matches[1];
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(downloadUrl);
      document.body.removeChild(a);
      return { success: true };
    } catch (err: any) {
      console.error('Error downloading telemetry table ZIP:', err);
      return { success: false, error: err.message || 'Export failed' };
    }
  },

  async downloadFullTelemetryCsv(params: {
    plant_id?: string;
    start_date?: string;
    end_date?: string; dates?: string; start_datetime?: string; end_datetime?: string;
    table?: 'axonmetric_livedaycons' | 'axonmetric_livestringinv' | 'plant' | 'inverter' | 'central inverter' | 'scb';
    inverter?: string;
  }): Promise<void> {
    await this.downloadFullTelemetryZip(params);
  },
};

